import { navigateToAdminFormTemplateEditor } from '@monorepo/shared/externalLinks/adminPanel';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { useFeatureFlags } from 'mapistry-shared/api';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FullScreenLoadingPage } from '../../../../shared/componentsV2/Loading';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { FormTemplateEditor } from '../FormTemplateEditor/FormTemplateEditor';
import { EditableFormTemplate } from '../FormTemplateEditor/types';
import { WizardStep } from '../shared/consts';
import { useNavigateToFormTemplateWizardStep } from '../shared/useNavigateToFormTemplateWizardStep';
import { GenerateFormTemplateInputs } from './GenerateFormTemplateInputs';
import { useFormTemplateCreate } from './hooks/useFormTemplateCreate';
import { useGenerateFormTemplateAndListenForPushMessageResponse } from './hooks/useGenerateFormTemplateAndListenForPushMessageResponse';

type SaveNewFormTemplateProps = {
  formTemplateSlug: string;
  isUsingOrgSpecificEditor: boolean;
  formWizardBasePath: string;
};

/**
 * if someone refreshes on the fields page, save won't work because they need to fill out the name,
 * so in that case punt them back to the basic info page
 */
function usePuntToDetailsPageIfNotFilledOut(formWizardBasePath: string) {
  const {
    formTemplate: { name },
  } = useFormTemplateContext();

  const navigateToDetailsStep = useNavigateToFormTemplateWizardStep({
    stepToGoTo: WizardStep.DETAILS,
    formTemplateSlug: null,
    formWizardBasePath,
  });

  useEffect(() => {
    if (!name) navigateToDetailsStep();
  }, [name, navigateToDetailsStep]);
}

/**
 * todo@formTemplates: once site admins can _create_ form templates, they need to be
 * sent to the EDIT step and no the calendars step
 */
function useNavigateAfterSavingNewFormTemplate({
  formTemplateSlug,
  isUsingOrgSpecificEditor,
  formWizardBasePath,
}: SaveNewFormTemplateProps): () => void {
  const { isAssigningFormCalendarsEnabled } = useFeatureFlags();

  const navigateToAdminEditor = useCallback(
    () => navigateToAdminFormTemplateEditor(formTemplateSlug),
    [formTemplateSlug],
  );
  const navigateToOrgSpecificCalendarEditor =
    useNavigateToFormTemplateWizardStep({
      stepToGoTo: WizardStep.CALENDARS,
      formTemplateSlug,
      formWizardBasePath,
    });
  const navigateToOrgSpecificFormEditor = useNavigateToFormTemplateWizardStep({
    stepToGoTo: WizardStep.FIELDS,
    formTemplateSlug,
    formWizardBasePath,
  });

  if (!isUsingOrgSpecificEditor) {
    return navigateToAdminEditor;
  }

  return isAssigningFormCalendarsEnabled
    ? navigateToOrgSpecificCalendarEditor
    : navigateToOrgSpecificFormEditor;
}

function useSubmitNewFormTemplate({
  formTemplate,
  organizationId,
  formWizardBasePath,
  resetDirtyState,
}: {
  formTemplate: EditableFormTemplate;
  organizationId: string | undefined;
  formWizardBasePath: string;
  resetDirtyState: () => void;
}) {
  const { isAssigningFormCalendarsEnabled } = useFeatureFlags();
  const isUsingOrgSpecificEditor = !!organizationId;

  const userFacingMessage = isAssigningFormCalendarsEnabled
    ? 'Your form has been created. You can now assign it to sites.'
    : 'Your form has been created. You can continue to edit the form. To assign the form to site(s), contact your customer support manager or help@mapistry.com. Soon, you’ll be able to do this yourself, but in the meantime, reach out to us.';

  const successToastMessage = isUsingOrgSpecificEditor
    ? userFacingMessage
    : 'You are getting redirected to the admin editor to finish setting up the form.';

  const navigateOnSuccess = useNavigateAfterSavingNewFormTemplate({
    formTemplateSlug: formTemplate.slug,
    formWizardBasePath,
    isUsingOrgSpecificEditor,
  });

  return useFormTemplateCreate({
    organizationId,
    // marking as not-dirty so that navigating super admins to the admin-panel won't
    // trigger navigation prevention
    prepForNavigationOnSuccess: resetDirtyState,
    successToastMessage,
    navigateOnSuccess,
  });
}

export function GenerateFormTemplatePage({
  formWizardBasePath,
}: {
  formWizardBasePath: string;
}) {
  useDisableSubnav();
  const { organizationId } = useParams();

  usePuntToDetailsPageIfNotFilledOut(formWizardBasePath);

  const { isLoading, formTemplate, resetDirtyState } = useFormTemplateContext();
  const [customPrompt, setCustomPrompt] = useState<string | undefined>();

  const { isProcessing, handleUpload, startedProcessingAt } =
    useGenerateFormTemplateAndListenForPushMessageResponse({
      organizationId,
    });

  const [saveNewFormTemplate, { isLoading: isSubmitting }] =
    useSubmitNewFormTemplate({
      formTemplate,
      organizationId,
      formWizardBasePath,
      resetDirtyState,
    });

  if (isLoading) return <FullScreenLoadingPage />;

  return (
    <FormTemplateEditor
      formWizardBasePath={formWizardBasePath}
      isEditingDisabled={isProcessing}
      isEditMode={false}
      isNavigatingInWizardEnabled={!isProcessing}
      isSubmitting={isSubmitting}
      onSaveFormTemplate={saveNewFormTemplate}
      organizationId={organizationId}
      sidebarWidth="50%"
      sidebarComponent={
        <GenerateFormTemplateInputs
          handleUpload={handleUpload}
          customPrompt={customPrompt}
          setCustomPrompt={setCustomPrompt}
          isProcessing={isProcessing}
          startedProcessingAt={startedProcessingAt}
        />
      }
    />
  );
}
